import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import Swal from 'sweetalert2'

import { useThemeMode } from '../../../../../../_metronic/partials'
import { ISelectOption } from '../../../../../common/types/select'
import { createProfile, updateProfile } from '../core/api'
import { readApiError } from '../core/errors'
import { FieldDescriptor, MatchingProfile, ProfileSchema, ValidationError } from '../core/types'
import { ProfileWriteBody } from '../core/requests.types'
import { isFieldShown, withShownWithDefaults } from '../core/shownWith'
import { SchemaField, themedSelectStyles } from './SchemaField'
import { escapeHtml } from '../../../../../common/escapeHtml'

interface Props {
  show: boolean
  onClose: () => void
  editing?: MatchingProfile | null
  schema: ProfileSchema
  merchantOptions: ISelectOption<number>[]
  bankOptions: ISelectOption<string>[]
  methodOptions: ISelectOption<number>[]
  onSaved: () => void
}

type ValueMap = Record<string, unknown>

const GROUP_TITLES: Record<string, string> = {
  timeouts: 'Таймауты',
  limits: 'Лимиты',
  scoring: 'Скоринг',
  isolation: 'Изоляция мерчантов',
  other: 'Прочее',
}

// Isolation lists come in allow/deny pairs and the allow side WINS in matching
// (`normalizeSide`: a non-empty allow list makes the rule an allow-set and the deny
// list is ignored outright). Filling both therefore produces a silently dead deny
// list, so the form locks the counterpart as soon as one side has values and says
// which side did the locking. Clearing the filled list unlocks the other.
const ISOLATION_PAIRS: Record<string, { counterpart: string; side: 'allow' | 'deny' }> = {
  ACCEPT_DEPOSITS_FROM_MERCHANTS: { counterpart: 'DONT_ACCEPT_DEPOSITS_FROM_MERCHANTS', side: 'allow' },
  DONT_ACCEPT_DEPOSITS_FROM_MERCHANTS: { counterpart: 'ACCEPT_DEPOSITS_FROM_MERCHANTS', side: 'deny' },
  SEND_DEPOSITS_TO_MERCHANTS: { counterpart: 'DONT_SEND_DEPOSITS_TO_MERCHANTS', side: 'allow' },
  DONT_SEND_DEPOSITS_TO_MERCHANTS: { counterpart: 'SEND_DEPOSITS_TO_MERCHANTS', side: 'deny' },
}

const isFilled = (v: unknown) => Array.isArray(v) && v.length > 0

const forDirection = (fields: FieldDescriptor[], direction: string) =>
  fields.filter((f) => !f.directions || f.directions.length === 0 || f.directions.includes(direction))

// A section is a heading with a hairline underline — no boxes.
const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({
  title,
  hint,
  children,
}) => (
  <div className='mb-8'>
    <div className='d-flex align-items-baseline border-bottom border-gray-200 pb-2 mb-4'>
      <span className='fw-bold text-gray-800 fs-5'>{title}</span>
      {hint && <span className='text-gray-500 fs-8 ms-3'>{hint}</span>}
    </div>
    {children}
  </div>
)

const LABEL = 'form-label fw-semibold text-gray-800 fs-7 mb-1'

export const ProfileFormModal: React.FC<Props> = ({
  show,
  onClose,
  editing,
  schema,
  merchantOptions,
  bankOptions,
  methodOptions,
  onSaved,
}) => {
  const { mode } = useThemeMode()
  const isEdit = !!editing
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [merchantId, setMerchantId] = useState<number | null>(null)
  const [direction, setDirection] = useState(schema.directions[0] ?? 'PAYOUT')
  const [priority, setPriority] = useState(1)
  const [isActive, setIsActive] = useState(true)
  const [filters, setFilters] = useState<ValueMap>({})
  const [config, setConfig] = useState<ValueMap>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!show) return
    setFieldErrors({})
    setFormError(null)
    if (editing) {
      setCode(editing.code)
      setName(editing.name)
      setMerchantId(editing.merchant_id)
      setDirection(editing.direction)
      setPriority(editing.priority)
      setIsActive(editing.is_active)
      setFilters({ ...(editing.filters ?? {}) })
      setConfig(withShownWithDefaults({ ...(editing.config ?? {}) }, schema.config))
    } else {
      setCode('')
      setName('')
      setMerchantId(null)
      setDirection(schema.directions[0] ?? 'PAYOUT')
      setPriority(1)
      setIsActive(true)
      setFilters({})
      setConfig({})
    }
  }, [show, editing, schema.directions, schema.config])

  const visibleFilters = useMemo(() => forDirection(schema.filters, direction), [schema.filters, direction])
  const visibleConfig = useMemo(() => forDirection(schema.config, direction), [schema.config, direction])

  // A direction with no filters at all gets a compact modal: rendering the full
  // payout layout around an empty «Фильтры» section reads as if something failed to
  // load. Derived from the schema, never from the string 'PAYMENT' — a PAYMENT
  // profile does carry a filter since CPAYMENT-1695 (payment_method), so it renders
  // the full form, and the compact one returns by itself if the schema ever leaves a
  // direction filterless again.
  const compact = visibleFilters.length === 0

  const configGroups = useMemo(() => {
    const order: string[] = []
    const byGroup: Record<string, FieldDescriptor[]> = {}
    visibleConfig.forEach((f) => {
      const g = f.group ?? 'other'
      if (!byGroup[g]) {
        byGroup[g] = []
        order.push(g)
      }
      byGroup[g].push(f)
    })
    return order.map((g) => ({ group: g, fields: byGroup[g] }))
  }, [visibleConfig])

  const pruneForDirection = (map: ValueMap, catalog: FieldDescriptor[], dir: string): ValueMap => {
    const allowed = new Set(forDirection(catalog, dir).map((f) => f.key))
    const next: ValueMap = {}
    Object.keys(map).forEach((k) => {
      if (allowed.has(k)) next[k] = map[k]
    })
    return next
  }

  const changeDirection = (dir: string) => {
    setDirection(dir)
    setFilters((prev) => pruneForDirection(prev, schema.filters, dir))
    setConfig((prev) => pruneForDirection(prev, schema.config, dir))
  }

  const setMapValue =
    (setter: React.Dispatch<React.SetStateAction<ValueMap>>, catalog: FieldDescriptor[]) => (key: string, val: unknown) =>
      setter((prev) => {
        const next = { ...prev }
        if (val === undefined || val === '' || (Array.isArray(val) && val.length === 0)) delete next[key]
        else next[key] = val
        return withShownWithDefaults(next, catalog)
      })

  const applyErrors = (errors: ValidationError[]) => {
    const map: Record<string, string> = {}
    errors.forEach((e) => {
      ;(e.fields ?? []).forEach((f) => {
        map[f] = e.message
      })
    })
    setFieldErrors(map)
    // Surface the reason via SweetAlert (project-wide convention) — some errors
    // (profile conflict / ambiguity) point at whole-object fields like `filters`
    // that have no single input to highlight, so a field mark alone is invisible.
    // This endpoint is carved out of the interceptor's generic error toast, so this
    // is the only popup.
    const messages = Array.from(new Set(errors.map((e) => e.message).filter(Boolean)))
    Swal.fire(
      messages.length
        ? { title: 'Ошибка', html: messages.map(escapeHtml).join('<br/>'), icon: 'error' }
        : { title: 'Ошибка', text: 'Не удалось сохранить профиль', icon: 'error' }
    )
  }

  const submit = async () => {
    setFieldErrors({})
    setFormError(null)
    if (!merchantId) {
      setFormError('Выберите мерчанта')
      return
    }
    const body: ProfileWriteBody = {
      code,
      name,
      merchant_id: merchantId,
      direction,
      priority,
      is_active: isActive,
      filters,
      config,
    }
    setSaving(true)
    try {
      const resp = isEdit ? await updateProfile(editing!.id, body) : await createProfile(body)
      if (resp.data.success) {
        onSaved()
        onClose()
      } else if (resp.data.errors?.length) {
        applyErrors(resp.data.errors)
      } else {
        Swal.fire({ title: 'Ошибка', text: 'Не удалось сохранить профиль', icon: 'error' })
      }
    } catch (e: any) {
      const { errors, message } = readApiError(e)
      if (errors.length) applyErrors(errors)
      else Swal.fire({ title: 'Ошибка', text: message, icon: 'error' })
    } finally {
      setSaving(false)
    }
  }

  // A pair member is locked only while its counterpart holds values and it does not.
  const lockReason = (key: string, values: ValueMap): string | undefined => {
    const pair = ISOLATION_PAIRS[key]
    if (!pair || isFilled(values[key]) || !isFilled(values[pair.counterpart])) return undefined
    return pair.side === 'allow'
      ? 'Заблокировано исключающим списком — очистите его, чтобы задать разрешающий'
      : 'Заблокировано разрешающим списком — он перекрывает исключения'
  }

  const grid = (fields: FieldDescriptor[], values: ValueMap, setter: React.Dispatch<React.SetStateAction<ValueMap>>, prefix: string) => (
    <div className='row g-5'>
      {fields.filter((f) => isFieldShown(f, values)).map((f) => (
        <div className='col-md-6' key={f.key}>
          <SchemaField
            field={f}
            value={values[f.key]}
            merchantOptions={merchantOptions}
            bankOptions={bankOptions}
            methodOptions={methodOptions}
            error={fieldErrors[`${prefix}.${f.key}`]}
            disabledReason={lockReason(f.key, values)}
            onChange={setMapValue(setter, prefix === 'config' ? schema.config : schema.filters)}
          />
        </div>
      ))}
    </div>
  )

  return (
    <Modal show={show} onHide={onClose} size={compact ? undefined : 'lg'} centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Редактирование профиля' : 'Новый профиль'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className='px-7 py-5'>
        {formError && (
          <div className='alert alert-danger' style={{ whiteSpace: 'pre-line' }}>
            {formError}
          </div>
        )}

        {/* Основные */}
        <Section title='Основные'>
          <div className='row g-5'>
            <div className='col-md-6'>
              <label className={LABEL}>Код</label>
              <input
                className={`form-control form-control-sm${fieldErrors['code'] ? ' is-invalid' : ''}`}
                value={code}
                disabled={isEdit}
                onChange={(e) => setCode(e.target.value)}
              />
              {fieldErrors['code'] && <div className='text-danger fs-8 mt-1'>{fieldErrors['code']}</div>}
            </div>
            <div className='col-md-6'>
              <label className={LABEL}>Название</label>
              <input className='form-control form-control-sm' value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className='col-md-6'>
              <label className={LABEL}>Направление</label>
              <select
                className='form-select form-select-sm'
                value={direction}
                onChange={(e) => changeDirection(e.target.value)}
              >
                {schema.directions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-md-6'>
              <label className={LABEL}>Основной мерчант</label>
              <Select
                options={merchantOptions}
                classNamePrefix='react-select'
                placeholder='Выберите мерчанта…'
                noOptionsMessage={() => 'Нет мерчантов'}
                menuPortalTarget={document.body}
                menuPosition='fixed'
                styles={themedSelectStyles(mode)}
                value={merchantOptions.find((o) => o.value === merchantId) ?? null}
                onChange={(o) => setMerchantId((o as ISelectOption<number> | null)?.value ?? null)}
              />
              {fieldErrors['merchant_id'] && (
                <div className='text-danger fs-8 mt-1'>{fieldErrors['merchant_id']}</div>
              )}
            </div>
            <div className='col-md-6'>
              <label className={LABEL}>Приоритет</label>
              <input
                className='form-control form-control-sm'
                style={{ maxWidth: 120 }}
                type='text'
                inputMode='numeric'
                value={priority}
                onChange={(e) => {
                  if (/^\d*$/.test(e.target.value)) setPriority(e.target.value === '' ? 0 : parseInt(e.target.value, 10))
                }}
              />
            </div>
            <div className='col-md-6'>
              <label className={`${LABEL} d-block`}>Статус</label>
              <div className='form-check form-check-solid form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='profile-active'
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <label className='form-check-label ms-2 text-gray-700' htmlFor='profile-active'>
                  {isActive ? 'Активен' : 'Выключен'}
                </label>
              </div>
            </div>
          </div>
        </Section>

        {/* Фильтры */}
        {!compact && (
          <Section title='Фильтры' hint='условия применимости профиля'>
            {grid(visibleFilters, filters, setFilters, 'filters')}
          </Section>
        )}

        {/* Параметры */}
        <Section
          title='Параметры'
          hint={compact ? 'изоляция мерчантов' : 'переопределения config поверх дефолтов'}
        >
          {configGroups.map(({ group, fields }, idx) => (
            <div key={group} className={idx > 0 ? 'mt-6' : ''}>
              <div className='text-gray-500 text-uppercase fw-bold fs-8 mb-4'>{GROUP_TITLES[group] ?? group}</div>
              {grid(fields, config, setConfig, 'config')}
            </div>
          ))}
        </Section>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light' onClick={onClose} disabled={saving}>
          Отмена
        </button>
        <button type='button' className='btn btn-primary' onClick={submit} disabled={saving}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}
